import { useEventListener, useLatestRef } from 'hellospa/common/hooks';
import React from 'react';
import { HeapProperties } from './heap';

export enum MessageType {
  EditorContinue = 'editor:continue',
  EditorAutoSave = 'editor:autoSave',
  EditorAutoSaveDone = 'editor:autoSaveDone',
  EditorValidation = 'editor:editorValidation',
  MobileValidation = 'mobile:editorValidation',
  MobileContinue = 'mobile:continue',
  MobileHeapEvent = 'mobile:heapEvent',
  SharePoint = 'sharepoint',
  // used in jquery.hellofax.js
  InternalUserLogout = 'internal:userLogout',
  // used in jquery.hellofax.js
  InternalSessionTimeoutWarning = 'internal:sessionTimeoutWarning',
  IntegrationUserLogout = 'mobile:userLogout',
  IntegrationSessionTimeoutWarning = 'mobile:sessionTimeoutWarning',
}

type EditorContinue = { type: MessageType.EditorContinue }

type EditorAutoSave = { type: MessageType.EditorAutoSave }

type EditorAutoSaveDone = { type: MessageType.EditorAutoSaveDone }

export type EditorValidationType = {
  type: MessageType.EditorValidation,
  isBackDisabled: boolean,
  isValid: boolean,
  numFields: number,
  errorMessage?: string,
}

type MobileValidationType = {
  type: MessageType.MobileValidation,
  isValid: boolean,
  numFields: number,
  errorMessage?: string,
}
type MobileContinue = Omit<MobileValidationType, 'type'> & {
  type: MessageType.MobileContinue,
}

type SharePointType = { type: MessageType.SharePoint }

type InternalUserLogout = { type: MessageType.InternalUserLogout }
type InternalSessionTimeoutWarning = { type: MessageType.InternalSessionTimeoutWarning }

type IntegrationUserLogout = { type: MessageType.IntegrationUserLogout }
type IntegrationSessionTimeoutWarning = { type: MessageType.IntegrationSessionTimeoutWarning }

type MobileHeapEvent = {
  type: MessageType.MobileHeapEvent,
  eventName: string,
  properties: HeapProperties
}

type Messages = EditorContinue | EditorAutoSave | EditorAutoSaveDone
  | EditorValidationType | SharePointType | MobileContinue | MobileValidationType
  | InternalUserLogout | InternalSessionTimeoutWarning | IntegrationUserLogout
  | IntegrationSessionTimeoutWarning | MobileHeapEvent

type Options = {
  /**
   * By default useWindowMessage only accepts messages from its own origin. This
   * works great for making sure we only get messages from other HelloSign code.
   * I had to add this with a wild card so that the mobile app can post messages
   */
  expectedOrigin?: '*' | string,
}

export function useWindowMessage<T extends Messages['type']>(
  type: T,
  callback: (m: Extract<Messages, { type: T }>, source: MessageEvent['source']) => void,
  { expectedOrigin = window.location.origin }: Options = {},
) {
  const callbackRef = useLatestRef(callback);

  React.useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      if (
        expectedOrigin === '*'
        || expectedOrigin === event.origin
      ) {
        if (event.data && event.data.type === type) {
          callbackRef.current(event.data, event.source);
        }
      }
    };

    window.addEventListener('message', onMessage);

    return () => window.removeEventListener('message', onMessage);
  }, [callbackRef, expectedOrigin, type]);
  return null;
}


export function postTo(
  targetWindow: Window,
  message: Messages,
  // This just needs to postMessage to hellosign-web code through an iframe and
  // it needs to work in Storybook. I think the simplest solution is to just
  // always post to the same origin.
  origin = window.location.origin,
) {
  targetWindow.postMessage(message, origin);
}

export const canPostToParent = (parentUrl: string | null): parentUrl is string => (
  parentUrl != null
  // In situation such as P&S is opened in new a popup window prevent sending
  // message back to the window itself if it's same as parent
  && window !== window.parent
);

/**
 * This is used to notify the mobile app if the user gets a timeout warning.
 * The timeout and alert are managed in jquery.hellofax.js. That code fires a
 * custom event, which is picked up here and forwarded to the parent from
 * Editor->P&S. Then P&S also uses this hook, so it listens for that message and
 * forwards to its parent P&S->parent page.
 */
export function useForwardSessionTimeout(parentUrl: string | null) {
  useEventListener(document, 'sessionTimeoutWarning', () => {
    if (parentUrl == null) return;
    postTo(window.parent, {
      type: MessageType.IntegrationSessionTimeoutWarning,
    }, parentUrl);
  });

  useWindowMessage(MessageType.IntegrationSessionTimeoutWarning, () => {
    if (parentUrl == null) {
      return;
    }

    postTo(window.parent, {
      type: MessageType.IntegrationSessionTimeoutWarning,
    }, parentUrl);
  });

  useEventListener(document, 'userLogout', (event) => {
    if (parentUrl == null) return;

    // Notify the dispatcher the event is forwarded
    // by canceling the userLogout event
    event.preventDefault();

    postTo(window.parent, {
      type: MessageType.IntegrationUserLogout,
    }, parentUrl);
  });
  useWindowMessage(MessageType.IntegrationUserLogout, () => {
    if (parentUrl == null) {
      return;
    }

    postTo(window.parent, {
      type: MessageType.IntegrationUserLogout,
    }, parentUrl);
  });
}
